import { useState, useEffect } from 'react'
import {
  BookOpen, Plus, Edit3, Search, ChevronRight, Check, X, Clock,
  AlertCircle, Tag, Loader, Star, Puzzle
} from 'lucide-react'
import type { Competency } from '../engine/types'
import { listCompetencies, createCompetency, updateCompetency } from '../engine/CompetencyStudio'
import { useLang } from '../i18n/LangContext'

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  active:  { bg: 'rgba(74,222,128,0.12)', text: 'var(--color-success)', label: 'Active' },
  draft:   { bg: 'rgba(251,191,36,0.12)', text: 'var(--color-warning)', label: 'Draft' },
  deprecated: { bg: 'rgba(248,113,113,0.12)', text: 'var(--color-danger)', label: 'Deprecated' },
}

interface NewDimensionForm {
  name: string
  name_en: string
  weight: number
  description: string
}

interface NewCompetencyForm {
  name: string
  name_cn: string
  description: string
  domain_id: string
  dimensions: NewDimensionForm[]
}

const emptyForm: NewCompetencyForm = {
  name: '',
  name_cn: '',
  description: '',
  domain_id: '',
  dimensions: [{ name: '', name_en: '', weight: 0, description: '' }],
}

export default function CompetencyStudioPage() {
  const { t } = useLang()
  const [competencies, setCompetencies] = useState<Competency[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<NewCompetencyForm>(emptyForm)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    async function fetch() {
      try {
        setLoading(true)
        setError(null)
        const data = await listCompetencies()
        if (cancelled) return
        setCompetencies(data)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load competencies')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    fetch()
    return () => { cancelled = true }
  }, [])

  const filtered = competencies.filter(c => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      c.name.toLowerCase().includes(q) ||
      c.name_cn.toLowerCase().includes(q) ||
      c.domain_id.toLowerCase().includes(q)
    )
  })

  function toggleExpand(id: string) {
    setExpandedId(prev => prev === id ? null : id)
  }

  function openCreateModal() {
    setEditingId(null)
    setForm(emptyForm)
    setSubmitError(null)
    setShowModal(true)
  }

  function openEditModal(comp: Competency) {
    setEditingId(comp.id)
    setForm({
      name: comp.name,
      name_cn: comp.name_cn,
      description: comp.description,
      domain_id: comp.domain_id,
      dimensions: comp.dimensions.map(d => ({
        name: d.name,
        name_en: d.name_en,
        weight: d.weight,
        description: d.description,
      })),
    })
    setSubmitError(null)
    setShowModal(true)
  }

  function addDimension() {
    setForm(prev => ({
      ...prev,
      dimensions: [...prev.dimensions, { name: '', name_en: '', weight: 0, description: '' }],
    }))
  }

  function removeDimension(idx: number) {
    if (form.dimensions.length <= 1) return
    setForm(prev => ({
      ...prev,
      dimensions: prev.dimensions.filter((_, i) => i !== idx),
    }))
  }

  function updateDimension(idx: number, field: keyof NewDimensionForm, value: string | number) {
    setForm(prev => ({
      ...prev,
      dimensions: prev.dimensions.map((d, i) => i === idx ? { ...d, [field]: value } : d),
    }))
  }

  async function handleSubmit() {
    if (!form.name || !form.name_cn) {
      setSubmitError('Name and Chinese name are required')
      return
    }
    try {
      setSubmitting(true)
      setSubmitError(null)
      const totalWeight = form.dimensions.reduce((sum, d) => sum + d.weight, 0)
      const normalizedDimensions = form.dimensions.map(d => ({
        ...d,
        weight: totalWeight > 0 ? d.weight / totalWeight : 1 / form.dimensions.length,
      }))

      const payload: Partial<Competency> = {
        name: form.name,
        name_cn: form.name_cn,
        description: form.description,
        domain_id: form.domain_id,
        dimensions: normalizedDimensions.map((d, i) => ({
          id: d.name_en.toLowerCase().replace(/\s+/g, '_') || `dim_${i}`,
          name: d.name,
          name_en: d.name_en,
          weight: d.weight,
          description: d.description,
          expected_behaviors: [],
        })),
      }

      if (editingId) {
        await updateCompetency(editingId, payload)
      } else {
        await createCompetency(payload)
      }

      const data = await listCompetencies()
      setCompetencies(data)
      setShowModal(false)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save competency')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <Loader size={32} className="animate-spin" style={{ color: 'var(--color-accent)' }} />
          <span style={{ color: 'var(--color-text-secondary)' }}>{t('common.loading')}</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3 max-w-md text-center p-6 rounded-xl" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}>
          <AlertCircle size={32} style={{ color: 'var(--color-danger)' }} />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{t('common.error')}</h2>
          <p style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{t('comp.title')}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>{t('comp.subtitle')}</p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
        >
          <Plus size={16} />
          {t('comp.new')}
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-muted)' }} />
        <input
          type="text"
          placeholder={t('comp.search')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg text-sm outline-none transition-colors"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            color: 'var(--color-text-primary)',
          }}
          onFocus={e => { e.currentTarget.style.borderColor = 'var(--color-accent)' }}
          onBlur={e => { e.currentTarget.style.borderColor = 'var(--color-border)' }}
        />
      </div>

      {/* Competency List */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border p-12 text-center" style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}>
          <BookOpen size={32} style={{ color: 'var(--color-text-muted)', margin: '0 auto' }} />
          <p className="mt-3 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {searchQuery ? 'No competencies match your search.' : 'No competencies defined yet. Create your first competency model.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(comp => {
            const status = statusConfig[comp.status] ?? statusConfig.draft
            const isExpanded = expandedId === comp.id
            const totalWeight = comp.dimensions.reduce((s, d) => s + d.weight, 0)

            return (
              <div
                key={comp.id}
                className="rounded-xl border transition-colors"
                style={{ backgroundColor: 'var(--color-bg-card)', borderColor: isExpanded ? 'var(--color-accent)' : 'var(--color-border)' }}
              >
                {/* Card Header */}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap mb-1">
                        <h3 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>{comp.name}</h3>
                        <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{comp.name_cn}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: status.bg, color: status.text }}>{status.label}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Tag size={12} style={{ color: 'var(--color-text-muted)' }} />
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{comp.domain_id}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => openEditModal(comp)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: 'var(--color-text-secondary)' }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.color = 'var(--color-accent)' }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--color-text-secondary)' }}
                      >
                        <Edit3 size={15} />
                      </button>
                      <button
                        onClick={() => toggleExpand(comp.id)}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: 'var(--color-text-secondary)' }}
                        onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.color = 'var(--color-accent)' }}
                        onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--color-text-secondary)' }}
                      >
                        <ChevronRight size={15} className="transition-transform" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }} />
                      </button>
                    </div>
                  </div>

                  {/* Dimension Weight Bars */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {comp.dimensions.map(dim => {
                      const pct = totalWeight > 0 ? (dim.weight / totalWeight) * 100 : 0
                      return (
                        <div key={dim.id} className="flex items-center gap-1.5">
                          <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, backgroundColor: 'var(--color-accent)' }}
                            />
                          </div>
                          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{dim.name_en}</span>
                        </div>
                      )
                    })}
                  </div>

                  {/* Stats Row */}
                  <div className="flex items-center gap-6 mt-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    <span className="flex items-center gap-1"><Puzzle size={12} />{comp.task_count} {t('comp.tasks')}</span>
                    <span className="flex items-center gap-1"><Star size={12} />{comp.rubric_count} {t('comp.rubrics')}</span>
                    <span className="flex items-center gap-1"><BookOpen size={12} />{comp.reference_case_count} {t('comp.cases')}</span>
                    <span className="flex items-center gap-1"><Clock size={12} />{new Date(comp.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Expanded Dimension Details */}
                {isExpanded && (
                  <div className="border-t px-5 py-4" style={{ borderColor: 'var(--color-border)' }}>
                    <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>{t('comp.dimensions')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {comp.dimensions.map(dim => {
                        const pct = totalWeight > 0 ? ((dim.weight / totalWeight) * 100).toFixed(1) : '0.0'
                        return (
                          <div
                            key={dim.id}
                            className="rounded-lg p-3 border"
                            style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{dim.name}</span>
                              <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-accent-glow)', color: 'var(--color-accent)' }}>{pct}%</span>
                            </div>
                            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{dim.name_en}</span>
                            <p className="text-xs mt-1.5 line-clamp-2" style={{ color: 'var(--color-text-secondary)' }}>{dim.description}</p>
                            {dim.expected_behaviors.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {dim.expected_behaviors.map((b, i) => (
                                  <span key={i} className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-card)', color: 'var(--color-text-muted)' }}>{b}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4" onClick={() => setShowModal(false)}>
          <div className="fixed inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }} />
          <div
            className="relative w-full max-w-2xl rounded-xl border shadow-2xl max-h-[80vh] overflow-y-auto"
            style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
              <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {editingId ? 'Edit Competency' : 'New Competency'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: 'var(--color-text-secondary)' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)' }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>Name (English) *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                    placeholder="e.g. Strategic Decision Making"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>Name (Chinese) *</label>
                  <input
                    type="text"
                    value={form.name_cn}
                    onChange={e => setForm(prev => ({ ...prev, name_cn: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                    placeholder="e.g. 战略决策能力"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>Domain</label>
                <input
                  type="text"
                  value={form.domain_id}
                  onChange={e => setForm(prev => ({ ...prev, domain_id: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                  placeholder="e.g. Business Strategy"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-secondary)' }}>Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                  placeholder="Brief description of this competency..."
                />
              </div>

              {/* Dimensions */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>{t('comp.dimensions')}</label>
                  <button
                    onClick={addDimension}
                    className="inline-flex items-center gap-1 text-xs font-medium transition-colors"
                    style={{ color: 'var(--color-accent)' }}
                  >
                    <Plus size={12} /> Add
                  </button>
                </div>
                <div className="space-y-3">
                  {form.dimensions.map((dim, idx) => (
                    <div
                      key={idx}
                      className="rounded-lg p-3 border"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border)' }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium" style={{ color: 'var(--color-text-muted)' }}>Dimension {idx + 1}</span>
                        {form.dimensions.length > 1 && (
                          <button
                            onClick={() => removeDimension(idx)}
                            className="p-0.5 rounded transition-colors"
                            style={{ color: 'var(--color-text-muted)' }}
                            onMouseEnter={e => { e.currentTarget.style.color = 'var(--color-danger)' }}
                            onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-text-muted)' }}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <input
                          type="text"
                          value={dim.name}
                          onChange={e => updateDimension(idx, 'name', e.target.value)}
                          className="w-full px-2.5 py-1.5 rounded text-xs outline-none"
                          style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                          placeholder="Name (CN)"
                        />
                        <input
                          type="text"
                          value={dim.name_en}
                          onChange={e => updateDimension(idx, 'name_en', e.target.value)}
                          className="w-full px-2.5 py-1.5 rounded text-xs outline-none"
                          style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                          placeholder="Name (EN)"
                        />
                        <input
                          type="number"
                          value={dim.weight}
                          onChange={e => updateDimension(idx, 'weight', parseFloat(e.target.value) || 0)}
                          min={0}
                          max={10}
                          step={0.1}
                          className="w-full px-2.5 py-1.5 rounded text-xs outline-none"
                          style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                          placeholder="Weight"
                        />
                      </div>
                      <input
                        type="text"
                        value={dim.description}
                        onChange={e => updateDimension(idx, 'description', e.target.value)}
                        className="w-full mt-2 px-2.5 py-1.5 rounded text-xs outline-none"
                        style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                        placeholder="Description"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {submitError && (
                <div className="flex items-center gap-2 text-sm p-3 rounded-lg" style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: 'var(--color-danger)' }}>
                  <AlertCircle size={14} />
                  {submitError}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ color: 'var(--color-text-secondary)' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)' }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent' }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-accent)', color: '#fff' }}
              >
                {submitting ? (
                  <Loader size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
                {editingId ? t('common.save') : t('common.create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}